import React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { StatsCalendar } from './StatsCalendar';
import { StatsMonthGrid } from './StatsMonthGrid';
import { StatsYearSelector } from './StatsYearSelector';

interface StatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPeriodSelect: (period: 'day' | 'month' | 'year' | 'all') => void;
  onDateSelect: (date: string) => void;
}

export const StatsModal: React.FC<StatsModalProps> = ({
  isOpen,
  onClose,
  onPeriodSelect,
  onDateSelect
}) => {
  const { t, i18n } = useTranslation();
  const [currentView, setCurrentView] = React.useState<'period' | 'calendar' | 'month-grid' | 'year-select'>('period');
  const [selectedPeriod, setSelectedPeriod] = React.useState<'day' | 'month' | 'year' | 'all' | null>(null);
  const [currentYear, setCurrentYear] = React.useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = React.useState(new Date().getMonth() + 1);

  // Русские месяцы
  const ruMonths = [
    '', 'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
  ];
  
  const ruMonthsShort = [
    '', 'Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн',
    'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'
  ];

  // Английские месяцы
  const enMonths = [
    '', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const enMonthsShort = [
    '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const months = i18n.language === 'ENG' ? enMonths : ruMonths;
  const monthsShort = i18n.language === 'ENG' ? enMonthsShort : ruMonthsShort;

  // Сбрасываем состояние при каждом открытии модального окна
  React.useEffect(() => {
    if (isOpen) {
      setCurrentView('period');
      setSelectedPeriod(null);
      // Устанавливаем текущий год и месяц, но не меньше 2025
      const now = new Date();
      const year = Math.max(now.getFullYear(), 2025);
      setCurrentYear(year);
      setCurrentMonth(now.getMonth() + 1);
    }
  }, [isOpen]);

  const handlePeriodSelect = (period: 'day' | 'month' | 'year' | 'all') => {
    setSelectedPeriod(period);
    
    if (period === 'all') {
      // Для "За всё время" сразу отправляем запрос
      onPeriodSelect(period);
      onClose();
    } else if (period === 'day') {
      // Устанавливаем текущий месяц и год, но не меньше 2025
      const now = new Date();
      const year = Math.max(now.getFullYear(), 2025);
      setCurrentYear(year);
      setCurrentMonth(now.getMonth() + 1);
      setCurrentView('calendar');
    } else if (period === 'month') {
      // Устанавливаем текущий год, но не меньше 2025
      const now = new Date();
      const year = Math.max(now.getFullYear(), 2025);
      setCurrentYear(year);
      setCurrentView('month-grid');
    } else if (period === 'year') {
      setCurrentView('year-select');
    }
  };

  const handleDateSelect = (date: string) => {
    onDateSelect(date);
    onClose();
  };

  const handleBack = () => {
    if (currentView === 'calendar' || currentView === 'month-grid' || currentView === 'year-select') {
      setCurrentView('period');
      setSelectedPeriod(null);
    }
  };

  const handleClose = () => {
    setCurrentView('period');
    setSelectedPeriod(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-header border border-border rounded-lg shadow-lg max-w-md w-full mx-4 max-h-[90vh] overflow-hidden">
                {/* Header */}
        <div className="pl-2 pr-1 py-2 border-b border-border">
          <div className="grid grid-cols-7 gap-1">
            <div></div>
            <div className="col-span-5 flex items-center justify-center">
              <h2 className="text-lg font-semibold text-white">
                {t('statistics')}
              </h2>
            </div>
            <div className="flex justify-center">
              <button
                onClick={handleClose}
                className="w-8 h-8 text-sm rounded-md transition-colors border border-border text-textSecondary focus:outline-none focus:ring-0 flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {currentView === 'period' && (
            <div className="space-y-3">
              <div className="text-sm text-white mb-4">
                {t('select_period')}
              </div>
              
                             <button
                 onClick={() => handlePeriodSelect('day')}
                 className="w-full p-3 rounded-lg border border-border bg-header text-white transition-colors focus:outline-none focus:ring-0"
               >
                 <span>{t('day')}</span>
               </button>
              
                             <button
                 onClick={() => handlePeriodSelect('month')}
                 className="w-full p-3 rounded-lg border border-border bg-header text-white transition-colors focus:outline-none focus:ring-0"
               >
                 <span>{t('month')}</span>
               </button>
              
                             <button
                 onClick={() => handlePeriodSelect('year')}
                 className="w-full p-3 rounded-lg border border-border bg-header text-white transition-colors focus:outline-none focus:ring-0"
               >
                 <span>{t('year')}</span>
               </button>
              
                             <button
                 onClick={() => handlePeriodSelect('all')}
                 className="w-full p-3 rounded-lg border border-border bg-header text-white transition-colors focus:outline-none focus:ring-0"
               >
                 <span>{t('all_time')}</span>
               </button>
            </div>
          )}

          {currentView === 'calendar' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <button
                  onClick={handleBack}
                  className="text-textSecondary transition-colors border border-border rounded px-2 py-1 focus:outline-none focus:ring-0"
                >
                  &lt; {t('back')}
                </button>
                <div></div>
                <div></div>
              </div>
              
              <div className="text-sm text-white">
                {t('select_day')}
              </div>
              
              <div className="flex justify-center">
              
              <StatsCalendar
                year={currentYear}
                month={currentMonth}
                months={months}
                onDateSelect={handleDateSelect}
                onMonthChange={(year, month) => {
                  setCurrentYear(year);
                  setCurrentMonth(month);
                }}
              />
              </div>
            </div>
          )}

          {currentView === 'month-grid' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <button
                  onClick={handleBack}
                  className="text-textSecondary transition-colors border border-border rounded px-2 py-1 focus:outline-none focus:ring-0"
                >
                  &lt; {t('back')}
                </button>
                <div></div>
                <div></div>
              </div>
              
              <div className="text-sm text-white">
                {t('select_month')}
              </div>
              
              <StatsMonthGrid
                year={currentYear}
                onMonthSelect={(year, month) => {
                  const dateString = `${year}-${month.toString().padStart(2, '0')}`;
                  handleDateSelect(dateString);
                }}
                onYearChange={(year) => setCurrentYear(year)}
              />
            </div>
          )}

          {currentView === 'year-select' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <button
                  onClick={handleBack}
                  className="text-textSecondary transition-colors border border-border rounded px-2 py-1 focus:outline-none focus:ring-0"
                >
                  &lt; {t('back')}
                </button>
                <div></div>
                <div></div>
              </div>
              
              <div className="text-sm text-white">
                {t('select_year')}
              </div>
              
              <StatsYearSelector
                currentYear={currentYear}
                onYearSelect={(year) => {
                  const dateString = year.toString();
                  handleDateSelect(dateString);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 